import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { teardownHeroManifestoIntegration } from './camst-hero-manifesto.js';

gsap.registerPlugin(ScrollTrigger);

// Allineato a App.tsx interactionTl (hero pin +=120%, scrub 1.5).
var MANIFESTO_PIN = {
  panelIn: 0.1,
  panelDuration: 1,
  wordsIn: 0.6,
  wordsDuration: 1,
  wordsStagger: 0.02,
  deepDiveIn: 1,
  deepDiveDuration: 0.5,
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function getManifestoTargets(root) {
  var scope = root;

  if (root.dataset.camstManifestoHeroIntegrated === 'true') {
    scope = document.querySelector('.camst-hero--has-manifesto') || root;
  }

  return {
    words: scope.querySelectorAll('.camst-manifesto__word-inner'),
    richHeadline: scope.querySelector('[data-camst-headline-rich]'),
    deepDive: scope.querySelector('.camst-manifesto__deep-dive'),
    panel: scope.querySelector('.camst-manifesto__panel'),
  };
}

function resetManifestoState(root) {
  var targets = getManifestoTargets(root);

  gsap.set(targets.words, { clearProps: 'transform' });

  if (targets.richHeadline) {
    gsap.set(targets.richHeadline, { clearProps: 'all' });
  }

  gsap.set(targets.panel, { clearProps: 'transform' });

  if (targets.deepDive) {
    gsap.set(targets.deepDive, { clearProps: 'all' });
  }
}

export function setManifestoVisible(root) {
  var targets = getManifestoTargets(root);

  gsap.set(targets.words, { yPercent: 0 });

  if (targets.richHeadline) {
    gsap.set(targets.richHeadline, { opacity: 1, y: 0 });
  }

  gsap.set(targets.panel, { yPercent: 0, y: 0, clearProps: 'transform' });
  root.classList.add('is-complete');

  if (targets.deepDive) {
    gsap.set(targets.deepDive, { opacity: 1, y: 0 });
  }
}

export function animateManifestoHeadline(timeline, targets, position, duration, stagger) {
  if (targets.words.length) {
    timeline.to(
      targets.words,
      {
        yPercent: 0,
        stagger: stagger,
        duration: duration,
        ease: 'power2.out',
      },
      position
    );
    return;
  }

  if (targets.richHeadline) {
    timeline.fromTo(
      targets.richHeadline,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: duration * 0.7,
        ease: 'power2.out',
      },
      position
    );
  }
}

export function syncManifestoTimeline(root, timeline) {
  if (!timeline || !timeline.scrollTrigger) {
    return;
  }

  ScrollTrigger.refresh();

  var progress = timeline.scrollTrigger.progress;

  timeline.progress(progress);

  if (progress >= 0.85) {
    root.classList.add('is-complete');
  }
}

function initManifesto(root) {
  if (!root || root.dataset.camstManifestoReady === 'true') {
    return;
  }

  if (root.dataset.camstManifestoHeroIntegrated === 'true') {
    return;
  }

  root.dataset.camstManifestoReady = 'true';

  var targets = getManifestoTargets(root);
  var mode = root.dataset.animation || 'pin_overlay';
  var hasHeadline = targets.words.length || targets.richHeadline;

  if (!hasHeadline || !targets.panel) {
    return;
  }

  if (prefersReducedMotion()) {
    setManifestoVisible(root);
    return;
  }

  root.classList.add('is-animating');

  gsap.set(targets.words, { yPercent: 101 });

  if (targets.richHeadline) {
    gsap.set(targets.richHeadline, { opacity: 0, y: 24 });
  }

  if (targets.deepDive) {
    gsap.set(targets.deepDive, { opacity: 0, y: 40 });
  }

  if (mode === 'reveal') {
    gsap.set(targets.panel, { yPercent: 0, y: 0 });

    var revealTl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top 80%',
        once: true,
        id: 'camst-manifesto-reveal-' + root.dataset.elementorId,
      },
      onComplete: function () {
        root.classList.add('is-complete');
      },
    });

    animateManifestoHeadline(revealTl, targets, 0, 0.9, MANIFESTO_PIN.wordsStagger);

    if (targets.deepDive) {
      revealTl.to(
        targets.deepDive,
        {
          opacity: 1,
          y: 0,
          duration: 0.55,
          ease: 'power2.out',
        },
        0.35
      );
    }

    syncManifestoTimeline(root, revealTl);

    return;
  }

  var pinViewport = root.querySelector('.camst-manifesto__pin-viewport');
  var pinLength = parseFloat(root.dataset.pinLength || '120') / 100;
  var scrub = parseFloat(root.dataset.scrub || '1.5');

  gsap.set(targets.panel, { yPercent: 100 });

  var pinTl = gsap.timeline({
    scrollTrigger: {
      trigger: root,
      start: 'top top',
      end: '+=' + Math.round(pinLength * 100) + '%',
      pin: pinViewport || targets.panel,
      scrub: scrub,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      id: 'camst-manifesto-pin-' + (root.id || 'widget'),
      onUpdate: function (self) {
        if (self.progress >= 0.85) {
          gsap.set(targets.panel, { yPercent: 0, y: 0, clearProps: 'transform' });
          root.classList.add('is-complete');
        }
      },
      onLeave: function () {
        gsap.set(targets.panel, { yPercent: 0, y: 0, clearProps: 'transform' });
        root.classList.add('is-complete');
      },
    },
  });

  pinTl.fromTo(
    targets.panel,
    { yPercent: 100 },
    {
      yPercent: 0,
      duration: MANIFESTO_PIN.panelDuration,
      ease: 'none',
    },
    MANIFESTO_PIN.panelIn
  );

  animateManifestoHeadline(
    pinTl,
    targets,
    MANIFESTO_PIN.wordsIn,
    MANIFESTO_PIN.wordsDuration,
    MANIFESTO_PIN.wordsStagger
  );

  if (targets.deepDive) {
    pinTl.fromTo(
      targets.deepDive,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: MANIFESTO_PIN.deepDiveDuration,
        ease: 'power2.out',
      },
      MANIFESTO_PIN.deepDiveIn
    );
  }

  syncManifestoTimeline(root, pinTl);

  window.requestAnimationFrame(function () {
    syncManifestoTimeline(root, pinTl);
  });

  window.addEventListener(
    'load',
    function onLoad() {
      syncManifestoTimeline(root, pinTl);
    },
    { once: true }
  );
}

function destroyManifesto(root) {
  if (!root) {
    return;
  }

  ScrollTrigger.getAll().forEach(function (st) {
    if (st.trigger === root || (st.trigger && root.contains(st.trigger))) {
      st.kill();
    }
  });

  resetManifestoState(root);
  delete root.dataset.camstManifestoReady;
  root.classList.remove('is-complete', 'is-animating');
}

export function initCamstManifesto(context) {
  var scope = context || document;
  var roots = scope.querySelectorAll('[data-camst-manifesto]');

  roots.forEach(function (root) {
    initManifesto(root);
  });
}

export function refreshCamstManifesto() {
  ScrollTrigger.refresh();
}

export function teardownCamstManifesto(context) {
  var scope = context || document;
  var roots = scope.querySelectorAll('[data-camst-manifesto]');

  roots.forEach(function (root) {
    teardownHeroManifestoIntegration(root);
    destroyManifesto(root);
  });
}
